import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Alert, Button, Card, Icon, Spinner, StatusPill } from '../components/ui';
import { ApiError, api } from '../lib/api';
import { brl, datePt, statusLabel } from '../lib/format';
import { usePageMeta } from '../lib/seo';
import { tr } from '../lib/i18n';

interface LookupAppointment {
  id: string;
  code: string;
  status: string;
  scheduledDate: string;
  startTime: string;
  serviceName: string;
  priceCents: number;
  businessName: string;
  businessSlug: string;
  professionalName: string;
}

interface LocationState {
  whatsapp?: string | null;
  calendarUrl?: string;
  message?: string;
}

function statusTone(status: string): 'neutral' | 'positive' | 'warning' | 'danger' {
  switch (status) {
    case 'confirmed':
    case 'completed':
      return 'positive';
    case 'pending':
      return 'warning';
    case 'cancelled_by_customer':
    case 'cancelled_by_professional':
    case 'no_show':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function ConfirmationPage(): JSX.Element {
  const { code = '' } = useParams();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;

  const [appointment, setAppointment] = useState<LookupAppointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageMeta(tr('Agendamento confirmado'), tr('Resumo do seu agendamento na AgendaPro.'), `/agendamento/confirmado/${code}`);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<{ appointment: LookupAppointment }>(`/api/public/appointment-lookup/${encodeURIComponent(code)}`)
      .then((res) => {
        if (alive) setAppointment(res.appointment);
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof ApiError ? err.message : tr('Agendamento não encontrado.'));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [code]);

  const calendarUrl = state.calendarUrl ?? null;

  return (
    <div className="mx-auto w-full max-w-content px-4 py-12">
      {loading ? (
        <div className="flex justify-center py-16"><Spinner label={tr("Consultando agendamento")} /></div>
      ) : error || !appointment ? (
        <Alert tone="danger" title={tr("Agendamento não encontrado")}>
          {error ?? tr('Não localizamos um agendamento com esse código.')} {tr("Verifique o código recebido por e-mail ou WhatsApp.")}
        </Alert>
      ) : (
        <div className="mx-auto max-w-2xl">
          <div className="text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-positive-soft text-graphite">
              <Icon name="check" size={28} />
            </span>
            <h1 className="mt-4 font-display text-3xl font-semibold text-ink">
              {appointment.status === 'pending' ? tr('Solicitação enviada') : tr('Agendamento confirmado')}
            </h1>
            <p className="mt-2 text-muted">
              {state.message ?? tr('Guarde o código abaixo para acompanhar ou cancelar seu agendamento.')}
            </p>
          </div>

          <Card as="section" className="mt-6">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted">{tr("Código")}</p>
              <p className="font-mono text-lg font-semibold text-ink">{appointment.code}</p>
            </div>
            <hr className="my-4 border-line" />
            <dl className="space-y-3 text-sm">
              <Row label={tr("Serviço")} value={appointment.serviceName} />
              <Row label={tr("Profissional")} value={appointment.professionalName} />
              <Row label={tr("Estabelecimento")} value={appointment.businessName} />
              <Row label={tr("Data")} value={datePt(appointment.scheduledDate)} />
              <Row label={tr("Horário")} value={appointment.startTime} />
              <Row label={tr("Valor")} value={brl(appointment.priceCents)} />
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted">{tr("Status")}</dt>
                <dd>
                  <StatusPill tone={statusTone(appointment.status)}>{statusLabel(appointment.status)}</StatusPill>
                </dd>
              </div>
            </dl>
          </Card>

          <Card as="section" className="mt-4">
            <h2 className="font-display text-lg font-semibold">{tr("Política de cancelamento")}</h2>
            <p className="mt-2 text-sm text-muted">
              {tr("As condições de cancelamento são definidas pelo profissional no momento do agendamento. Em caso de dúvida, fale diretamente com o estabelecimento pelos canais abaixo. Você também pode consultar a")}{' '}
              <Link to="/cancelamento" className="text-graphite underline">{tr("política geral de cancelamento da AgendaPro")}</Link>.
            </p>
            {appointment.status === 'pending' ? (
              <p className="mt-2 text-sm text-muted">
                {tr("Este agendamento ainda depende de aprovação do profissional. O horário fica protegido por um período limitado; se a solicitação expirar, escolha outro horário.")}
              </p>
            ) : null}
          </Card>

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {calendarUrl ? (
              <a href={calendarUrl} download>
                <Button variant="secondary" icon={<Icon name="calendar" />}>{tr("Adicionar ao calendário")}</Button>
              </a>
            ) : null}
            {state.whatsapp ? (
              <a href={state.whatsapp} target="_blank" rel="noopener noreferrer">
                <Button variant="secondary" icon={<Icon name="whatsapp" />}>{tr("Falar no WhatsApp")}</Button>
              </a>
            ) : null}
            <Link to="/">
              <Button variant="ghost">{tr("Voltar ao início")}</Button>
            </Link>
          </div>
          {state.whatsapp ? (
            <p className="mt-2 text-center text-xs text-muted">
              {tr("O botão abre a conversa no seu aplicativo de WhatsApp; a AgendaPro não envia mensagens automáticas.")}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right font-medium text-ink">{value}</dd>
    </div>
  );
}
